import {
  IcoCheckCircle,
  IcoCircle,
  IcoGlobe,
  IcoUser,
  IcoUsers,
} from "../../../../components/icons";
import {
  type MemberOption,
  MemberPicker,
} from "../../../../components/ui/member-picker";
import type { Group, QuestionTargetType } from "../../../../types";

type TargetingSectionProps = {
  targetType: QuestionTargetType;
  targetIds: string[];
  groups: Group[];
  members: MemberOption[];
  onChange: (targetType: QuestionTargetType, targetIds: string[]) => void;
};

export function TargetingSection({
  targetType,
  targetIds,
  groups,
  members,
  onChange,
}: TargetingSectionProps) {
  const handleTypeChange = (newType: QuestionTargetType) => {
    // Clear targetIds when changing type
    onChange(newType, []);
  };

  const handleTargetIdsChange = (newIds: string[]) => {
    onChange(targetType, newIds);
  };

  return (
    <fieldset class="flex flex-col gap-3">
      <legend class="text-sm font-medium text-text-secondary">
        Who should see this question?
      </legend>
      <p class="text-xs text-text-muted -mt-1">
        Target questions to specific groups or individuals. Great for
        role-specific questions or manager-only check-ins.
      </p>

      {/* Target type options */}
      <div class="flex flex-wrap gap-3 mt-1">
        <TargetTypeOption
          name="targeting"
          value="company_wide"
          checked={targetType === "company_wide"}
          onChange={() => handleTypeChange("company_wide")}
          label="Everyone"
          description="All team members"
          icon={<IcoGlobe class="size-5" />}
        />
        <TargetTypeOption
          name="targeting"
          value="group"
          checked={targetType === "group"}
          onChange={() => handleTypeChange("group")}
          label="Groups"
          description="Selected groups only"
          icon={<IcoUsers class="size-5" />}
          disabled={groups.length === 0}
        />
        <TargetTypeOption
          name="targeting"
          value="individual"
          checked={targetType === "individual"}
          onChange={() => handleTypeChange("individual")}
          label="Individuals"
          description="Specific people"
          icon={<IcoUser class="size-5" />}
        />
      </div>

      {/* Group picker */}
      {targetType === "group" && (
        <div class="mt-2">
          {groups.length === 0 ? (
            <p class="text-sm text-text-muted">
              No groups created yet.{" "}
              <a href="../groups" class="text-text underline">
                Create a group
              </a>{" "}
              to target questions.
            </p>
          ) : (
            <>
              <p class="text-sm text-text-muted mb-2">Select groups:</p>
              <GroupPicker
                groups={groups}
                selectedIds={targetIds}
                onChange={handleTargetIdsChange}
              />
              {targetIds.length === 0 && (
                <p class="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  Select at least one group
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Member picker */}
      {targetType === "individual" && (
        <div class="mt-2">
          <p class="text-sm text-text-muted mb-2">Select people:</p>
          <div class="max-h-64 overflow-hidden">
            <MemberPicker
              members={members}
              selectedIds={targetIds}
              onChange={handleTargetIdsChange}
            />
          </div>
          {targetIds.length === 0 && (
            <p class="text-xs text-amber-600 dark:text-amber-400 mt-2">
              Select at least one person
            </p>
          )}
        </div>
      )}
    </fieldset>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

type TargetTypeOptionProps = {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
  icon: preact.JSX.Element;
  disabled?: boolean;
};

function TargetTypeOption({
  name,
  value,
  checked,
  onChange,
  label,
  description,
  icon,
  disabled,
}: TargetTypeOptionProps) {
  return (
    <label
      class={`flex-1 min-w-[140px] flex gap-3 items-start rounded-lg border p-3 transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-ring-offset ${
        disabled
          ? "opacity-50 cursor-not-allowed border-border"
          : checked
            ? "border-text-subtle cursor-pointer"
            : "border-border hover:border-text-subtle cursor-pointer"
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        class="sr-only"
      />
      <div class={`mt-0.5 ${checked ? "text-text" : "text-text-subtle"}`}>
        {checked ? (
          <IcoCheckCircle class="size-5" />
        ) : (
          <IcoCircle class="size-5" />
        )}
      </div>
      <span class="flex flex-col gap-0.5">
        <span class="flex items-center gap-2">
          <span class={checked ? "text-text" : "text-text-muted"}>{icon}</span>
          <span class="font-medium text-text">{label}</span>
        </span>
        <span class="text-sm text-text-muted">{description}</span>
      </span>
    </label>
  );
}

type GroupPickerProps = {
  groups: Group[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
};

function GroupPicker({ groups, selectedIds, onChange }: GroupPickerProps) {
  const selectedSet = new Set(selectedIds);

  const toggle = (groupId: string) => {
    if (selectedSet.has(groupId)) {
      onChange(selectedIds.filter((id) => id !== groupId));
    } else {
      onChange([...selectedIds, groupId]);
    }
  };

  return (
    <div class="flex flex-wrap gap-2">
      {groups.map((group) => {
        const isSelected = selectedSet.has(group.id);
        return (
          <button
            key={group.id}
            type="button"
            onClick={() => toggle(group.id)}
            class={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              isSelected
                ? "bg-text text-surface"
                : "bg-surface-hover text-text-muted hover:text-text hover:bg-surface-active"
            }`}
          >
            {isSelected && (
              <svg
                class="size-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
            {group.name}
            <span class={isSelected ? "opacity-70" : "text-text-subtle"}>
              ({group.memberCount})
            </span>
          </button>
        );
      })}
    </div>
  );
}
